import React, { useState } from "react";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { productData } from "settings/constants/constants";
import { createKontrolTicket, createTicket } from "../request/request";
import { findProductByLabel } from "../utils/tickets";
import { RequestModalShell, type RequestStep } from "./requestForm/RequestModalShell";
import { SummaryFields } from "./requestForm/SummaryFields";
import { AttachmentDropzone } from "./requestForm/AttachmentDropzone";
import { ContactResponseFields } from "./requestForm/ContactResponseFields";
import {
	createEmptyContact,
	isContactValid,
	toContactPayload,
} from "./requestForm/contactDetails";
import { isDescriptionValid, isTitleValid } from "./requestForm/fieldRules";
import { useRequestSteps } from "./requestForm/useRequestSteps";
import { useTicketSubmission } from "./requestForm/useTicketSubmission";

interface FeatureRequestModalProps {
	isOpen: boolean;
	onClose: () => void;
	onTicketCreated?: () => void;
}

// The CTX Reflector desk still expects these two, though they never vary.
const CLOUD = "CloudGlobal";
const REGION = "us-east-1";

// A Kontrol Edge feature request is only a title and a description: the KS
// request type takes no attachments and no contact.
const KE_STEPS: RequestStep[] = [{ id: "details", title: "Details" }];
const REFLECTOR_STEPS: RequestStep[] = [
	{ id: "details", title: "Details" },
	{ id: "attachments", title: "Attachments & contact" },
];

export const FeatureRequestModal: React.FC<FeatureRequestModalProps> = ({
	isOpen,
	onClose,
	onTicketCreated,
}) => {
	const api = useAxiosAuth0();
	const globalProduct = useAppSelector(
		(state) => state.product.selectedProduct
	);
	const isKontrolEdge = globalProduct === "Kontrol Edge";

	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
	const [contact, setContact] = useState(createEmptyContact);
	const submission = useTicketSubmission(onTicketCreated);

	const steps = isKontrolEdge ? KE_STEPS : REFLECTOR_STEPS;
	const stepper = useRequestSteps(steps, {
		details: isTitleValid(title) && isDescriptionValid(description),
		attachments: isContactValid(contact),
	});

	const handleClose = () => {
		setTitle("");
		setDescription("");
		setAttachmentFiles([]);
		setContact(createEmptyContact());
		stepper.resetSteps();
		submission.resetSubmission();
		onClose();
	};

	const handleSubmit = () => {
		if (isKontrolEdge) {
			void submission.submit(() =>
				createKontrolTicket(api, {
					requesttype: "FEATURE_REQUEST",
					summary: title,
					description,
				})
			);
			return;
		}
		void submission.submit(() =>
			createTicket(api, {
				Tipo: "Feature Request",
				product: findProductByLabel(productData, globalProduct || "")?.value ?? "",
				cloud: CLOUD,
				region: REGION,
				Resumen: title,
				Descripcion: description,
				files: attachmentFiles,
				// Feature Request has always sent the contact, switch on or not.
				...toContactPayload(contact),
			})
		);
	};

	return (
		<RequestModalShell
			type="feature"
			isOpen={isOpen}
			onClose={handleClose}
			steps={steps}
			activeStepId={stepper.activeStepId}
			canActivate={stepper.canActivate}
			onStepChange={stepper.goToStep}
			canContinue={stepper.canContinue}
			isSaving={submission.isSaving}
			onSubmit={handleSubmit}
			submitError={submission.submitError}
			created={submission.created}
		>
			{stepper.activeStepId === "details" && (
				<SummaryFields
					title={title}
					description={description}
					onTitleChange={setTitle}
					onDescriptionChange={setDescription}
					titlePlaceholder="What would you like us to build?"
					descriptionPlaceholder="The problem it solves and how you imagine it working"
				/>
			)}

			{stepper.activeStepId === "attachments" && (
				<>
					<AttachmentDropzone
						files={attachmentFiles}
						onChange={setAttachmentFiles}
						hint="Screenshots, mockups or examples of what you have in mind."
					/>
					<ContactResponseFields contact={contact} onChange={setContact} />
				</>
			)}
		</RequestModalShell>
	);
};

export default FeatureRequestModal;
